/**
 * useCommercialAssumptions - Manages financial modeling assumptions
 *
 * These are INPUT parameters that affect all financial calculations:
 * - Analysis period (project lifecycle years)
 * - Discount rate (for NPV calculations)
 * - Tax rate (for incentive calculations)
 * - Escalation rates (energy, demand, fuel, labor)
 * - Degradation rate (PV output decline)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FINANCIAL_DEFAULTS } from '../config/defaults';

// Default escalation rates (% per year) - stored as percentages (e.g., 3.0 = 3%)
const DEFAULT_ESCALATION_RATES = {
    energy: 3.0,              // Utility energy charges - EIA Annual Energy Outlook average
    demand: 3.0,              // Utility demand charges - assumed to track energy escalation
    naturalGasDelivery: 2.5,  // Gas delivery charges - typically tracks inflation
    naturalGasCommodity: 3.0, // Gas commodity costs - more volatile, market-driven
    labor: 2.5                // Labor/O&M costs - inflation-linked
};

// Default tax incentive settings
const DEFAULT_TAX_INCENTIVES = {
    includeMacrs: false,           // Opt-in: MACRS depreciation tax shield
    macrsSchedule: '5-year',       // '5-year' (solar/storage) or '7-year'
    itcTreatment: 'upfront_reduction', // 'upfront_reduction' | 'year_one_credit' | 'transfer'
    itcTransferDiscountPercent: 10,    // Discount when selling/transferring ITC (% of ITC value)
    macrsBasisReductionPercent: 50,    // IRC Section 50(c): reduce MACRS basis by this % of ITC
};

// MACRS depreciation schedules (percentage of depreciable basis per year)
export const MACRS_SCHEDULES = {
    '5-year': [20.00, 32.00, 19.20, 11.52, 11.52, 5.76],    // IRS 5-year MACRS (6 tax years)
    '7-year': [14.29, 24.49, 17.49, 12.49, 8.93, 8.92, 8.93, 4.46], // IRS 7-year MACRS (8 tax years)
};

export function useCommercialAssumptions(initialState = null) {
    // Core financial parameters
    const [analysisYears, setAnalysisYears] = useState(
        initialState?.analysisYears ?? FINANCIAL_DEFAULTS.analysisYears.value
    );
    const [discountRate, setDiscountRate] = useState(
        initialState?.discountRate ?? FINANCIAL_DEFAULTS.discountRate.value * 100 // Store as percentage
    );
    const [taxRate, setTaxRate] = useState(
        initialState?.taxRate ?? FINANCIAL_DEFAULTS.taxRate.value * 100 // Store as percentage
    );
    const [degradationRate, setDegradationRate] = useState(
        initialState?.degradationRate ?? FINANCIAL_DEFAULTS.degradationRate.value * 100 // Store as percentage
    );

    // Escalation rates (% per year) - merge with defaults to ensure all properties exist
    // Backwards compatibility: if legacy naturalGas exists but not split rates, use it for both
    const [escalationRates, setEscalationRates] = useState(() => {
        const initial = initialState?.escalationRates || {};
        const merged = { ...DEFAULT_ESCALATION_RATES, ...initial };

        // Backwards compatibility: migrate legacy naturalGas to split rates
        if (initial.naturalGas !== undefined &&
            initial.naturalGasDelivery === undefined &&
            initial.naturalGasCommodity === undefined) {
            merged.naturalGasDelivery = initial.naturalGas;
            merged.naturalGasCommodity = initial.naturalGas;
        }

        return merged;
    });

    // Tax incentive settings (MACRS, ITC treatment)
    const [taxIncentives, setTaxIncentives] = useState(() => ({
        ...DEFAULT_TAX_INCENTIVES,
        ...(initialState?.taxIncentives || {})
    }));

    // Track the previous initialState to detect scenario switches
    const prevInitialStateRef = useRef(initialState);
    const [hasRestoredInitialState, setHasRestoredInitialState] = useState(false);

    // Reset restoration flag when initialState reference changes (scenario switch)
    useEffect(() => {
        if (initialState !== prevInitialStateRef.current) {
            prevInitialStateRef.current = initialState;
            setHasRestoredInitialState(false);
        }
    }, [initialState]);

    // Restore from initialState when it becomes available
    useEffect(() => {
        if (initialState && !hasRestoredInitialState) {
            if (initialState.analysisYears !== undefined) {
                setAnalysisYears(initialState.analysisYears);
            }
            if (initialState.discountRate !== undefined) {
                setDiscountRate(initialState.discountRate);
            }
            if (initialState.taxRate !== undefined) {
                setTaxRate(initialState.taxRate);
            }
            if (initialState.degradationRate !== undefined) {
                setDegradationRate(initialState.degradationRate);
            }
            if (initialState.escalationRates) {
                // Merge with defaults to ensure all properties exist
                setEscalationRates(prev => {
                    const initial = initialState.escalationRates;
                    const merged = { ...DEFAULT_ESCALATION_RATES, ...initial };

                    // Backwards compatibility: migrate legacy naturalGas to split rates
                    if (initial.naturalGas !== undefined &&
                        initial.naturalGasDelivery === undefined &&
                        initial.naturalGasCommodity === undefined) {
                        merged.naturalGasDelivery = initial.naturalGas;
                        merged.naturalGasCommodity = initial.naturalGas;
                    }

                    return merged;
                });
            }
            if (initialState.taxIncentives) {
                setTaxIncentives(prev => ({ ...DEFAULT_TAX_INCENTIVES, ...initialState.taxIncentives }));
            }

            setHasRestoredInitialState(true);
        }
    }, [initialState, hasRestoredInitialState]);

    // Computed values for calculator consumption (convert percentages to fractions)
    const calculatorInputs = useMemo(() => ({
        analysisYears,
        discountRate: discountRate / 100,
        taxRate: taxRate / 100,
        degradationRate: degradationRate / 100,
        escalationRates: {
            energy: escalationRates.energy / 100,
            demand: escalationRates.demand / 100,
            naturalGas: escalationRates.naturalGasDelivery / 100,  // backwards compat for consumers not yet updated
            naturalGasDelivery: escalationRates.naturalGasDelivery / 100,
            naturalGasCommodity: escalationRates.naturalGasCommodity / 100,
            labor: escalationRates.labor / 100
        },
        taxIncentives: {
            includeMacrs: taxIncentives.includeMacrs,
            macrsSchedule: taxIncentives.macrsSchedule,
            itcTreatment: taxIncentives.itcTreatment,
            itcTransferDiscount: taxIncentives.itcTransferDiscountPercent / 100,
            macrsBasisReduction: taxIncentives.macrsBasisReductionPercent / 100,
        }
    }), [analysisYears, discountRate, taxRate, degradationRate, escalationRates, taxIncentives]);

    // Serializable state for persistence
    const getState = useCallback(() => ({
        analysisYears,
        discountRate,
        taxRate,
        degradationRate,
        escalationRates,
        taxIncentives
    }), [analysisYears, discountRate, taxRate, degradationRate, escalationRates, taxIncentives]);

    // Reset to defaults
    const resetToDefaults = useCallback(() => {
        setAnalysisYears(FINANCIAL_DEFAULTS.analysisYears.value);
        setDiscountRate(FINANCIAL_DEFAULTS.discountRate.value * 100);
        setTaxRate(FINANCIAL_DEFAULTS.taxRate.value * 100);
        setDegradationRate(FINANCIAL_DEFAULTS.degradationRate.value * 100);
        setEscalationRates({ ...DEFAULT_ESCALATION_RATES });
        setTaxIncentives({ ...DEFAULT_TAX_INCENTIVES });
    }, []);

    return {
        // Individual values (displayed as percentages in UI)
        analysisYears,
        setAnalysisYears,
        discountRate,
        setDiscountRate,
        taxRate,
        setTaxRate,
        degradationRate,
        setDegradationRate,
        escalationRates,
        setEscalationRates,
        taxIncentives,
        setTaxIncentives,

        // Computed values for calculators (fractions)
        calculatorInputs,

        // Persistence helpers
        getState,
        resetToDefaults
    };
}

export default useCommercialAssumptions;
